import { z } from 'zod';
import {
  TaskType,
  taskTypeZodWithCatch,
} from 'packages/lib/api/annotations/contract/enums';
import {
  CostGroupQuery,
  groupTypeZod,
  GroupType,
  isTokenlessScope,
  toCostGroupQuery,
} from 'packages/lib/api/annotations-ops/contract/group-types';

// 2026-10-05 Used Cursor (Claude Opus 5.5 Medium) to add Contributor and Task views

/** Child group (or task) represented by one `data[]` row. */
export const analyticsRowGroupTypeZod = z.union([
  groupTypeZod,
  z.literal('task'),
]);
export type AnalyticsRowGroupType = z.infer<typeof analyticsRowGroupTypeZod>;

/**
 * Shared by `GET /cost/group`, `GET /cost/task` and `GET /cost/user`. Costs are summed
 * totals; per-unit costs are derived on the frontend from the labeling cost and counts.
 */
export const costSummaryZod = z.object({
  labelingCost: z.number(),
  industryCost: z.number(),
  qaCost: z.number(),
  auditCost: z.number(),
  totalCost: z.number(),
  numTasks: z.number(),
  /** Zero when no included TaskType has frames. */
  numFrames: z.number(),
  numAnnotations: z.number(),
});
export type CostSummaryData = z.infer<typeof costSummaryZod>;

/**
 * One row of `GET /cost/group`, covering every task under that
 * group that survived the startDate/endDate, taskType and managerEmail filters.
 */
export const costGroupItemZod = z.object({
  token: z.string(),
  name: z.string(),
  groupType: analyticsRowGroupTypeZod,
  /** So frontend can warn when averaged & summed values include more than one TaskType */
  includedTaskTypes: z.array(taskTypeZodWithCatch),
  labelingCost: z.number(),
  qaCost: z.number(),
  auditCost: z.number(),
  industryCost: z.number(),
  totalCost: z.number(),
  numTasks: z.number(),
  /** Null when this row has no frame dimension. */
  numFrames: z.number().nullable(),
  numAnnotations: z.number(),
});
export type CostGroupItem = z.infer<typeof costGroupItemZod>;

export const costGroupResponseZod = z.object({
  summary: costSummaryZod,
  data: z.array(costGroupItemZod),
});
export type CostGroupResponse = z.infer<typeof costGroupResponseZod>;

/** The phase of a task a contributor worked on. */
export const costRoleZod = z.enum(['Labeling', 'QA', 'Audit']);
export type CostRole = z.infer<typeof costRoleZod>;

/** One contributor's work on one phase of the task, i.e. one row of `GET /cost/task`. */
export const costTaskItemZod = z.object({
  /** Display name, i.e. "Firstname Lastname". */
  contributor: z.string(),
  /** Identifies the contributor's own analytics page. */
  userEmail: z.string(),
  taskPhase: costRoleZod,
  hoursWorked: z.number(),
  /** Null when the task's TaskType has no frames. */
  numFrames: z.number().nullable(),
  numAnnotations: z.number(),
  /** iMerit in-house tool cost for this contributor's phase. Industry cost cannot be split per contributor, so it is summary-only. */
  totalCost: z.number(),
});
export type CostTaskItem = z.infer<typeof costTaskItemZod>;

export const costTaskResponseZod = z.object({
  summary: costSummaryZod,
  data: z.array(costTaskItemZod),
});
export type CostTaskResponse = z.infer<typeof costTaskResponseZod>;

/** One completed task phase by the requested user, i.e. one row of `GET /cost/user`. */
export const costUserItemZod = z.object({
  taskToken: z.string(),
  taskType: taskTypeZodWithCatch,
  role: costRoleZod,
  hoursWorked: z.number(),
  /** The whole task's industry cost, not just this user's share of it. */
  industryCost: z.number(),
  /** Null when the task's TaskType has no frames. */
  numFrames: z.number().nullable(),
  numAnnotations: z.number(),
  /** This user's cost for their role on this task. */
  totalCost: z.number(),
});
export type CostUserItem = z.infer<typeof costUserItemZod>;

export const costUserResponseZod = z.object({
  summary: costSummaryZod,
  data: z.array(costUserItemZod),
});
export type CostUserResponse = z.infer<typeof costUserResponseZod>;

/**
 * `metric` query param of `GET /cost/time-series`, which decides the unit
 * of every value and, for `comparison`, the keys as well. Averages (`avgCost`,
 * `avgTasks`, `avgHours`) and throughput metrics are planned for later phases.
 */
export const costMetricZod = z.enum([
  'totalCost',
  'totalTasks',
  'totalHours',
  'comparison',
]);
export type CostMetric = z.infer<typeof costMetricZod>;

/** YYYY-MM-DD, the x-axis of every time-series point. */
const seriesDateZod = z.object({ date: z.string() });

/**
 * Keys are named for the role rather than the unit, each of these may represent dollars,
 * task counts, or hours depending on the requested CostMetric.
 */
const roleSeriesPointZod = seriesDateZod.extend({
  labeling: z.number(),
  qa: z.number(),
  audit: z.number(),
});
export type RoleSeriesPoint = z.infer<typeof roleSeriesPointZod>;

const costComparisonPointZod = seriesDateZod.extend({
  labeling: z.number(),
  industry: z.number(),
});
export type CostComparisonPoint = z.infer<typeof costComparisonPointZod>;

/**
 * One day of `GET /cost/time-series`. Points arrive in the shape Recharts
 * plots directly: one object per x-axis tick, one numeric key per line.
 */
export type CostSeriesPoint = RoleSeriesPoint | CostComparisonPoint;

/** Every key a line can be plotted from, i.e. every `dataKey` the chart may ask for. */
export type CostSeriesValueKey = Exclude<
  keyof (RoleSeriesPoint & CostComparisonPoint),
  'date'
>;

export const costListResponseZod = <T extends z.ZodTypeAny>(itemZod: T) =>
  z.object({ data: z.array(itemZod) });
export type CostListResponse<T> = { data: T[] };

/**
 * Given a CostMetric, returns the appropriate Zod parser for the API's response
 */
export const costSeriesResponseZodByMetric: Record<
  CostMetric,
  z.ZodType<CostListResponse<CostSeriesPoint>>
> = {
  totalCost: costListResponseZod(roleSeriesPointZod),
  totalTasks: costListResponseZod(roleSeriesPointZod),
  totalHours: costListResponseZod(roleSeriesPointZod),
  comparison: costListResponseZod(costComparisonPointZod),
};
export type CostSeriesResponse = CostListResponse<CostSeriesPoint>;

/** Page-level filters shared by `GET /cost/group`, `GET /cost/user` and `GET /cost/time-series`. */
export interface CostQueryFilters {
  /** YYYY-MM-DD */
  startDate: string;
  /** YYYY-MM-DD */
  endDate: string;
  /** Empty means all task types, in which case the param is omitted. */
  taskTypes: TaskType[];
  /** The signed-in user's email when the Contributors menu is on Direct Reports. */
  managerEmail?: string;
}

/** `GET /cost/group` only. */
export interface CostGroupFilters extends CostQueryFilters {
  /** Return one task per row, rather than aggregating */
  includeAllTasks?: boolean;
}

export type CostQuery = CostGroupQuery & CostGroupFilters;

export const toCostQuery = (
  groupType: GroupType,
  tokenOrId: string | undefined,
  filters: CostGroupFilters
): CostQuery | null => {
  const scope = toCostGroupQuery(groupType, tokenOrId);
  return scope ? { ...scope, ...filters } : null;
};

export type CostUserQuery = CostQueryFilters & { userEmail: string };

/**
 * `GET /cost/time-series` before the chart adds `metric`. Group levels and contributors
 * include the page filters; a task charts its whole life, so it takes none.
 */
export type CostSeriesBaseQuery =
  | ({ type: 'allpipelines' | 'allrequests' } & CostQueryFilters)
  | ({
      type: Exclude<GroupType, 'allpipelines' | 'allrequests'> | 'contributor';
      /** A group token matching `type`, the request ID for `requestID`, or the user's email for `contributor`. */
      tokenOrId: string;
    } & CostQueryFilters)
  | { type: 'task'; tokenOrId: string };

/** The base query plus the chart's selected metric. */
export type CostSeriesQuery = CostSeriesBaseQuery & { metric: CostMetric };

/** A group cost query, rewritten for the time-series endpoint. `metric` is left unset. */
export const toCostSeriesBaseQuery = (
  query: CostQuery
): CostSeriesBaseQuery => {
  const { startDate, endDate, taskTypes, managerEmail } = query;
  const filters: CostQueryFilters = {
    startDate,
    endDate,
    taskTypes,
    managerEmail,
  };
  if (isTokenlessScope(query)) {
    return { type: query.groupType, ...filters };
  }
  return { type: query.groupType, tokenOrId: query.tokenOrId, ...filters };
};
